import CommonTable from './CommonTable';
import ContextHelper from '../ContextHooks/ContextHelper';
import { getDataFromServer } from '../utils/apiBase';
import { useEffect, useState } from 'react';
import { Download } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function AddReport() {
  const { currentUser, setCurrentUser } = ContextHelper();
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!isDownloading) {
      const fetchData = () => {
        const today = new Date();
        const startDateTime = startDate ? new Date(startDate) : new Date(today);
        const endDateTime = endDate ? new Date(endDate) : new Date();
        startDateTime.setHours(0, 0, 0, 0);
        endDateTime.setHours(23, 59, 59, 999);
        const apiUrl = `apiv2/data?startdate=${startDateTime.toISOString()}&enddate=${endDateTime.toISOString()}`;

        const isString = (value) => typeof value === 'string';
        const getDisplayValue = (value) => {
          if (value == null || value === '') {
            return '';
          }

          if (typeof value === 'string' || typeof value === 'number') {
            return value;
          }

          if (Array.isArray(value)) {
            return value.map((item) => getDisplayValue(item)).filter(Boolean).join(', ');
          }

          if (typeof value === 'object') {
            if (typeof value.Alphabetic === 'string' && value.Alphabetic.trim()) {
              return value.Alphabetic;
            }

            return Object.values(value)
              .map((item) => getDisplayValue(item))
              .filter(Boolean)
              .join(', ');
          }

          return String(value);
        };

        const handleDataResponse = (response) => {
          if (response.status === 'success') {
            const formattedData = response.response.map((data) => {
              const parseStudyDate = (studyDate) => {
                const studyDateNumber = parseInt(studyDate, 10);
                const studyDateStr = studyDateNumber.toString();
                const studyYear = studyDateStr.slice(0, 4);
                const studyMonth = studyDateStr.slice(4, 6);
                const studyDay = studyDateStr.slice(6);
                return `${studyDay}-${studyMonth}-${studyYear}`;
              };

              const getReportedBy = (reports) => {
                const lastNonDeletedReport =
                  reports &&
                  reports
                    .slice()
                    .reverse()
                    .find((report) => !report.deleted);
                return lastNonDeletedReport ? lastNonDeletedReport.username : '';
              };

              const formattedStudyDate = parseStudyDate(data.StudyDate);
              const HistoryIMG = data.history.item ? data.history.item.length : '0';

              return {
                name: getDisplayValue(data.name),
                study: getDisplayValue(data.study),
                location: getDisplayValue(data.location),
                assignedTo: getDisplayValue(data.assignedTo),
                referingPhysicianName: getDisplayValue(data.referingPhysicianName),
                ReportedBy: getReportedBy(data.reports),
                StudyDate: formattedStudyDate,
                history: data.history.title ? data.history.title : isString(data.history) ? data.history : '',
                historyFiles: data.history.item ? data.history.item.map((itm) => ({ uri: itm.uri })) : [],
                historyTime: data.history.time ? data.history.time : '',
                drRemark: data.newDrRemark ? data.newDrRemark : data.drRemark,
                date: data.Date,
                isUrgent: data.isUrgent,
                isverifier: data.isverifier,
                delete: data.isDelete,
                reports: data.reports.filter((item) => item.deleted !== true),
                reportTime: data.reports[0] ? data.reports[0].time : '',
                id: data.id,
                images: data.images ? data.images : '0',
                HistoryIMG: HistoryIMG,
                source: getDisplayValue(data.source),
                modality: getDisplayValue(data.modality),
                patientID: getDisplayValue(data.patientID),
                contactNo: data?.contactNo,
                studyUUID: data.newStudyInstanceUID === 'NA' ? data.studyUUID : data.newStudyInstanceUID,
                reportApproved: data.reportApproved,
              };
            });

            formattedData.reverse();
            setIsDataFetched(true);
            setReportData(formattedData);
          } else {
            localStorage.clear();
            setCurrentUser({});
            navigate('/');
            setReportData([]);
          }
          setIsLoading(false);
        };

        const handleError = (error) => {
          console.error(error);
        };

        const params = {
          end_point: apiUrl,
          params: { token: currentUser?.token, type: currentUser?.type },
          call_back: handleDataResponse,
          props: '',
        };

        setIsLoading(true);
        getDataFromServer(params)
          .catch(handleError)
          .finally(() => setIsLoading(false));
      };

      fetchData();
      if (uploadSuccess) {
        fetchData();
      }

      const intervalId = setInterval(fetchData, 5 * 60 * 1000);

      return () => clearInterval(intervalId);
    }
  }, [startDate, endDate, uploadSuccess, currentUser?.token, isDownloading]);

  const isAdmin = currentUser?.isadmin;
  const isDoctor = currentUser?.type === 'Doctor';
  const isVerifier = currentUser?.type === 'verifier';
  const admin = currentUser?.type === 'admin';
  const superAdmin = currentUser?.type === 'superadmin';
  const allowedHeadersOptions = currentUser?.allowedHeader || [];
  
  let columns;
  
  if (isAdmin && !isVerifier) {
    // Standard admin columns
    columns = [
      { key: 'check', header: <Download />, defaultVisible: true },
      { key: 'tat', header: 'TAT', defaultVisible: true },
      { key: 'location', header: 'Location', defaultVisible: !isDoctor },
      { key: 'name', header: 'Name', defaultVisible: true },
      { key: 'patientID', header: 'PatientID', defaultVisible: true },
      { key: 'referingPhysicianName', header: 'Refering Physician', defaultVisible: true },
      { key: 'study', header: 'Study', defaultVisible: true },
      { key: 'report', header: 'Report', defaultVisible: true },
      { key: 'history', header: 'History', defaultVisible: true },
      { key: 'StudyDate', header: 'Study date', defaultVisible: true },
      { key: 'drRemark', header: 'Download', defaultVisible: true },
      { key: 'date', header: 'Upload Date', defaultVisible: true },
      { key: 'status', header: 'Status', defaultVisible: true },
      { key: 'images', header: 'Images', defaultVisible: true },
      { key: 'HistoryIMG', header: 'HistoryIMG', defaultVisible: false },
      { key: 'other', header: 'Viewer', defaultVisible: true },
      { key: 'modality', header: 'Modality', defaultVisible: true },
      { key: 'assignedTo', header: 'Assigned To', defaultVisible: true },
      { key: 'share', header: 'Share Report', defaultVisible: true },
      { key: 'source', header: 'Source', defaultVisible: false },
      { key: 'deleteCase', header: 'Delete Case', defaultVisible: true },
    ];
  } else if (isVerifier) {
    // Verifier columns with visibility based on allowedHeadersOptions
    // Start with base columns (always keep check/download column visible)
    columns = [{ key: 'check', header: <Download />, defaultVisible: true }];
  
    // Add all potential columns with visibility controlled by allowedHeadersOptions
    const columnsToFilter = [
      { key: 'tat', header: 'TAT' },
      { key: 'location', header: 'Location' },
      { key: 'name', header: 'Name' },
      { key: 'patientID', header: 'PatientID' },
      { key: 'referingPhysicianName', header: 'Refering Physician' },
      { key: 'study', header: 'Study' },
      { key: 'report', header: 'Report' },
      { key: 'history', header: 'History' },
      { key: 'StudyDate', header: 'Study date' },
      { key: 'drRemark', header: 'Download' },
      { key: 'date', header: 'Upload Date' },
      { key: 'status', header: 'Status' },
      { key: 'images', header: 'Images' },
      { key: 'modality', header: 'Modality' },
    ];
  
    // If also admin, add admin-specific columns to the filter list
    if (isAdmin) {
      columnsToFilter.push(
        { key: 'HistoryIMG', header: 'HistoryIMG' },
        { key: 'other', header: 'Viewer' },
        { key: 'assignedTo', header: 'Assigned To' },
        { key: 'share', header: 'Share Report' },
        { key: 'source', header: 'Source' }
      );
      
      // Add deleteCase column only for admin and superadmin
      if (admin || superAdmin) {
        columnsToFilter.push({ key: 'deleteCase', header: 'Delete Case' });
      }
    }
  
    // Filter and add columns based on allowedHeadersOptions
    columnsToFilter.forEach((col) => {
      if (allowedHeadersOptions.includes(col.header)) {
        columns.push({
          ...col,
          defaultVisible: true,
        });
      }
    });
  } else {
    // Standard user columns (not admin, not verifier)
    columns = [
      { key: 'check', header: <Download />, defaultVisible: true },
      { key: 'tat', header: 'TAT', defaultVisible: true },
      { key: 'location', header: 'Location', defaultVisible: !isDoctor },
      { key: 'name', header: 'Name', defaultVisible: true },
      { key: 'patientID', header: 'PatientID', defaultVisible: true },
      { key: 'referingPhysicianName', header: 'Refering Physician', defaultVisible: true },
      { key: 'study', header: 'Study', defaultVisible: true },
      { key: 'report', header: 'Report', defaultVisible: true },
      { key: 'history', header: 'History', defaultVisible: true },
      { key: 'StudyDate', header: 'Study date', defaultVisible: true },
      { key: 'drRemark', header: 'Download', defaultVisible: true },
      { key: 'date', header: 'Upload Date', defaultVisible: true },
      { key: 'status', header: 'Status', defaultVisible: true },
      { key: 'images', header: 'Images', defaultVisible: true },
      { key: 'modality', header: 'Modality', defaultVisible: true },
    ];
  }

  return (
    <section className="add-report-section">
      <CommonTable
        data={reportData}
        columns={columns}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        setIsDataFetched={setIsDataFetched}
        uploadSuccess={uploadSuccess}
        setUploadSuccess={setUploadSuccess}
        setIsDownloading={setIsDownloading}
        isDownloading={isDownloading}
      />
    </section>
  );
}

export default AddReport;
